/**
 * Thin @capacitor/network wrapper.
 *
 * `navigator.onLine` is unreliable inside a WebView (it reports the WebView's
 * notion of connectivity, not the device's, and on iOS frequently stays `true`
 * on a dead cellular link). On native we use @capacitor/network as the source of
 * truth; on web we fall back to navigator.onLine + the window online/offline
 * events. The health-ping in useNetworkHealth remains the ultimate authority —
 * this just feeds it a better "the radio is off" signal so it can react fast.
 */
import { isNative } from './platform'

let _Network = null
let _loadPromise = null

/**
 * Lazy-load @capacitor/network once. Never return the Capacitor plugin proxy
 * from a Promise chain — its Proxy implements .then via the plugin bridge, so
 * Promise assimilation calls Network.then() and throws on Android.
 */
function _ensureNetworkLoaded() {
  if (!isNative()) return Promise.resolve()
  if (_Network) return Promise.resolve()
  if (!_loadPromise) {
    _loadPromise = import('@capacitor/network')
      .then((mod) => {
        _Network = mod.Network
      })
      .catch((e) => {
        console.warn('[Network] @capacitor/network unavailable:', e?.message || e)
      })
  }
  return _loadPromise
}

async function _getPlugin() {
  if (!isNative()) return null
  await _ensureNetworkLoaded()
  return _Network
}

/**
 * Current connectivity. Resolves quickly; never throws.
 * @returns {Promise<boolean>}
 */
export async function isConnected() {
  const Network = await _getPlugin()
  if (!Network) return navigator.onLine
  try {
    const status = await Network.getStatus()
    return !!status.connected
  } catch {
    return navigator.onLine
  }
}

/**
 * Subscribe to connectivity changes.
 * @param {(connected: boolean, status: object) => void} cb
 * @returns {Promise<() => void>} unsubscribe function
 */
export async function onConnectivityChange(cb) {
  const Network = await _getPlugin()
  if (!Network) {
    const onOnline = () => cb(true, { connected: true })
    const onOffline = () => cb(false, { connected: false })
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }
  try {
    const handle = await Network.addListener('networkStatusChange', (status) => {
      cb(!!status.connected, status)
    })
    return () => { try { handle.remove() } catch { /* ignore */ } }
  } catch (e) {
    console.warn('[Network] addListener failed:', e?.message || e)
    return () => {}
  }
}
